/**
 * Minigames the web plays with the bot's rules (T3.3, S-3): the bet on the
 * shared odds (`shop/bet.ts`, odds from the global `fake/bet` document) and
 * Spot (`minigames/spot.ts`, the round in session memory, ADR-021). Chafer
 * and larva need other players and stay hidden until a lobby exists (P6).
 */
import { MINIGAMES } from '../balance.js';
import type { BetDoc } from '../bot/globals.js';
import { GameError } from '../errors.js';
import type { GameEvent } from '../events.js';
import { premiumOf } from '../premium.js';
import { randInt, type Rng } from '../rng.js';
import type { BotUser, Session } from '../state.js';
import { nextRollAt } from './attributes.js';

// ── Bet ──────────────────────────────────────────────────────────

/** Start of the odds window: the bot draws new odds at every odd hour (`dollarp/notif.ts#chooseFamily`). */
export function coteWindow(now: number): number {
  const d = new Date(now);
  const h = d.getHours();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), h % 2 ? h : h - 1, 0, 0).getTime();
}

/** `generateCote`: what the bot's scheduler writes to `fake/bet` (the web only reads it). */
export function rollCote(rng: Rng): number {
  const c = MINIGAMES.bet.cote;
  let cote = randInt(rng, c.min, c.max) / 100;
  if (cote === 1) cote = randInt(rng, c.boostMin, c.boostMax) / 100;
  return cote;
}

/** Chance to win, in percent, as the bot shows and rolls it. */
export function betWinChance(cote: number): number {
  return Math.floor((1 / cote) * 100);
}

/** Bets per 2-hour window: `1 + premium.bet + vote.bet` (`isUserCanUseCommandTime('bet')`). */
export function maxBets(save: BotUser, now: number): number {
  return MINIGAMES.bet.maxBets + premiumOf(save, now).bet + (save.vote?.bet ?? 0);
}

/** Bets left in the current 2-hour window. */
export function betsLeft(save: BotUser, now: number): number {
  const used = now > nextRollAt(save.bet.date) ? 0 : save.bet.time;
  return Math.max(0, maxBets(save, now) - used);
}

/** The bet menu's bounds: at least 5, at most half the purse. */
export function betLimits(save: BotUser): { min: number; max: number } {
  return { min: MINIGAMES.bet.minBid, max: Math.max(MINIGAMES.bet.minBid, Math.floor(save.money / 2)) };
}

/**
 * `betting`: one bet on the current shared odds; 1 % of lost bets are
 * refunded. `expectedCote` is what the player was shown: if the odds moved
 * since, the bet is refused instead of settling on odds they never saw.
 */
export function placeBet(
  save: BotUser,
  amount: number,
  odds: BetDoc | undefined,
  now: number,
  rng: Rng,
  events: GameEvent[],
  expectedCote?: number,
): void {
  if (!odds || !(odds.cote > 0)) throw new GameError('betClosed');
  if (expectedCote !== undefined && expectedCote !== odds.cote) throw new GameError('betOddsChanged', { cote: odds.cote });
  const bet = save.bet;
  const canRoll = nextRollAt(bet.date);
  if (now > canRoll) {
    bet.time = 0;
    bet.previous = [];
  }
  if (bet.time >= maxBets(save, now)) throw new GameError('betCooldown', { at: canRoll });
  if (!Number.isFinite(amount)) throw new GameError('invalidAmount', { amount });
  amount = Math.floor(amount);
  const limits = betLimits(save);
  if (amount < limits.min) throw new GameError('invalidAmount', { amount });
  if (save.money < amount) throw new GameError('notEnoughMoney', { have: save.money, needed: amount });
  if (amount > limits.max) throw new GameError('betTooHigh', { max: limits.max });

  bet.time += 1;
  const cote = odds.cote;
  const winChance = betWinChance(cote);
  let delta: number;
  const won = randInt(rng, 0, 101) <= winChance;
  if (won) {
    delta = Math.floor(amount * cote - amount);
    bet.win += delta;
    save.stats.bet.nbWin += 1;
    save.stats.money.total += delta;
  } else {
    if (winChance >= 98) save.stats.bet.loose98 += 1;
    if (winChance >= 95) save.stats.bet.loose95 += 1;
    delta = -amount;
    bet.loose += amount;
    save.stats.bet.nbLoose += 1;
  }
  save.money += delta;
  bet.nb += 1;
  bet.date = now;
  bet.previous.push(delta);
  const refund = !won && randInt(rng, 0, MINIGAMES.bet.refundRange) === 0;
  if (refund) save.money += amount;
  events.push({ type: 'betPlaced', amount, cote, winChance, won, delta, refund });
}

// ── Spot ─────────────────────────────────────────────────────────

/** `calcRewards`: the bid × 2 × 3 × … × (rounds + 1). */
export function spotReward(bid: number, rounds: number): number {
  let r = bid;
  for (let i = 1; i <= rounds; i++) r *= i + 1;
  return r;
}

/** Last round number (round r offers r + 1 monsters). */
export const SPOT_LAST_ROUND = MINIGAMES.spot.monsters - 1;

/** `fillSpotMenu`: a bid the player can cover; nothing is charged yet (the bot charges at the end). */
export function spotStart(save: BotUser, session: Session, bid: number, events: GameEvent[]): void {
  if (session.spot) throw new GameError('spotInProgress', {});
  if (!(MINIGAMES.spot.bids as readonly number[]).includes(bid)) throw new GameError('invalidAmount', { amount: bid });
  if (save.lootbox < bid) throw new GameError('notEnoughLootcoins', { have: save.lootbox, needed: bid });
  session.spot = { bid, round: 1 };
  events.push({ type: 'spotStarted', bid });
}

/**
 * `fillSpotCashout`. A wrong pick costs the bid (the bot decrements it even
 * if it was spent meanwhile). A cash-out pays `reward − bid`, unless the
 * player no longer holds the bid: the bot then calls them a cheater and
 * writes nothing.
 */
function settleSpot(save: BotUser, session: Session, won: boolean, events: GameEvent[]): void {
  const spot = session.spot;
  if (!spot) return;
  session.spot = null;
  const rounds = spot.round - 1;
  const s = save.stats.spot;
  if (won) {
    if (save.lootbox < spot.bid) {
      events.push({ type: 'spotEnded', bid: spot.bid, won, delta: 0, rounds, flagged: true });
      return;
    }
    const gain = spotReward(spot.bid, rounds) - spot.bid;
    save.lootbox += gain;
    s.win += gain;
    s.count.total += 1;
    s.count.win += 1;
    events.push({ type: 'spotEnded', bid: spot.bid, won, delta: gain, rounds, flagged: false });
  } else {
    save.lootbox -= spot.bid;
    s.loose += spot.bid;
    s.count.total += 1;
    s.count.loose += 1;
    events.push({ type: 'spotEnded', bid: spot.bid, won, delta: -spot.bid, rounds, flagged: false });
  }
}

/** `fillSpotGame`: guess which of the round's monsters hides the loot. */
export function spotPick(save: BotUser, session: Session, monster: number, rng: Rng, events: GameEvent[]): void {
  const spot = session.spot;
  if (!spot) throw new GameError('noSpot', {});
  const round = spot.round;
  if (round > SPOT_LAST_ROUND) throw new GameError('wrongPhase', {});
  if (!Number.isInteger(monster) || monster < 0 || monster > round) throw new GameError('invalidTarget', { target: monster });
  const right = randInt(rng, 0, round + 1);
  const correct = monster === right;
  events.push({ type: 'spotPicked', round, monster, right, correct });
  if (correct) spot.round = round + 1;
  else settleSpot(save, session, false, events);
}

/** Take the bid × multiplier of the rounds won so far. */
export function spotCashout(save: BotUser, session: Session, events: GameEvent[]): void {
  const spot = session.spot;
  if (!spot) throw new GameError('noSpot', {});
  if (spot.round <= 1) throw new GameError('wrongPhase', {});
  settleSpot(save, session, true, events);
}
